'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import Switch from '@repo/editor/src/components/Switch/Switch';
import { withEventScope } from '@repo/event-processor';
import LoadingIcon from '@repo/ui/components/Icon/LoadingIcon';
import { P2 } from '@repo/ui/components/Typography';
import { themeStyle } from '@repo/ui/theming';

import AiTextToolInput from './AiTextToolInput';
import AiTextToolMainPrompt from './AiTextToolMainPrompt';
import CTA from './CTA';
import { JsonFieldType } from './schema';
import {
  Form,
  CenteredSpinnerWrapper,
  FieldsGrid,
  RequiredFields,
  SwitchWrapper,
} from './styles';
import {
  AiTextToolFormProps,
  FieldTypeToValueType,
  FormData,
  JsonField,
} from './types';
import {
  buildFormData,
  resetAdvancedFields,
  validateRequiredFieldByKey,
} from './utils';

const REQUIRED_FIELD_ERROR = 'This field is required';

const AiTextToolForm: React.FC<AiTextToolFormProps> = ({
  formSchema,
  onSubmit,
  isLoading,
}) => {
  const [formData, setFormData] = useState<FormData | null>(null);
  const [mainPrompt, setMainPrompt] = useState('');
  const [showAdvancedFields, setShowAdvancedFields] = useState(false);
  const [errors, setErrors] = useState<Record<JsonField['key'], string>>({});

  useEffect(() => {
    const formData = buildFormData(formSchema);
    setFormData(formData);
    setMainPrompt(formSchema.textArea.default ?? '');
  }, [formSchema]);

  const validateField = useCallback(
    <T extends JsonFieldType>(
      key: keyof FormData,
      value: FieldTypeToValueType[T]
    ) => {
      const isFieldValid = validateRequiredFieldByKey(key, value, formSchema);

      setErrors((prev) => ({
        ...prev,
        [key]: isFieldValid ? '' : REQUIRED_FIELD_ERROR,
      }));
    },
    [formSchema]
  );

  const validateForm = useCallback(() => {
    if (!formData) {
      return false;
    }

    let result = true;

    Object.entries(formData).forEach(([key, value]) => {
      const isFieldValid = validateRequiredFieldByKey(key, value, formSchema);

      if (!isFieldValid) {
        result = false;
      }

      setErrors((prev) => ({
        ...prev,
        [key]: isFieldValid ? '' : REQUIRED_FIELD_ERROR,
      }));
    });

    if (!mainPrompt) {
      result = false;
      setErrors((prev) => ({
        ...prev,
        mainPrompt: REQUIRED_FIELD_ERROR,
      }));
    }

    return result;
  }, [formData, formSchema, mainPrompt]);

  const handleSetMainPrompt = useCallback((value: string) => {
    setMainPrompt(value);

    setErrors((prev) => ({
      ...prev,
      mainPrompt: value ? '' : REQUIRED_FIELD_ERROR,
    }));
  }, []);

  const handleShowAdvancedFields = useCallback(
    (prev: boolean) => {
      if (formData && formSchema) {
        setFormData(resetAdvancedFields(formData, formSchema));
      }

      return !prev;
    },
    [formData, formSchema]
  );

  const handleChangeFields = useCallback(
    <T extends JsonFieldType>(
      key: keyof FormData,
      value: FieldTypeToValueType[T]
    ) => {
      setFormData((prevState) =>
        prevState === null
          ? null
          : {
              ...prevState,
              [key]: value,
            }
      );

      validateField<T>(key, value);
    },
    [validateField]
  );

  const handleSubmit = async (event: React.SyntheticEvent) => {
    event.preventDefault();

    const isFormValid = validateForm();

    if (!onSubmit || !formData || !isFormValid) {
      return;
    }

    await onSubmit({
      formData,
      formSchema,
      mainUserPrompt: mainPrompt,
    });
  };

  const { requiredFields, optionalFields } = useMemo(
    () => ({
      requiredFields: formSchema.fields.filter((field) => field.required) ?? [],
      optionalFields:
        formSchema.fields.filter((field) => !field.required) ?? [],
    }),
    [formSchema]
  );

  const isCTADisabled = useMemo(
    () => Object.values(errors).some((error) => !!error),
    [errors]
  );

  if (!formData) {
    return (
      <CenteredSpinnerWrapper
        theme={{ spinnerColor: themeStyle.varBrandMedium }}
      >
        <LoadingIcon height="50px" />
      </CenteredSpinnerWrapper>
    );
  }

  return (
    <Form onSubmit={handleSubmit}>
      <AiTextToolMainPrompt
        error={errors.mainPrompt}
        isLoading={isLoading}
        textArea={formSchema.textArea}
        value={mainPrompt}
        onChange={handleSetMainPrompt}
      />
      <RequiredFields>
        {requiredFields.map((field) => {
          const { key } = field;
          const error = errors[key];
          const value = formData[key] ?? field.default;

          return (
            <AiTextToolInput
              key={key}
              disabled={isLoading}
              error={error}
              field={field}
              value={value}
              onChange={handleChangeFields}
            />
          );
        })}
      </RequiredFields>
      <SwitchWrapper>
        {/* this div is needed for a proper positioning. Without it the Switch is attached to the left side */}
        <div>
          <Switch
            large
            checked={showAdvancedFields}
            disabled={isLoading}
            label="Advanced settings"
            theme={{ padding: '0' }}
            onChange={() =>
              setShowAdvancedFields((prev) => handleShowAdvancedFields(prev))
            }
          />
        </div>
        <P2>Advanced settings</P2>
      </SwitchWrapper>
      {showAdvancedFields && (
        <FieldsGrid>
          {optionalFields.map((field) => {
            const { key } = field;
            const error = errors[key];
            const value = formData[key] ?? field.default;

            return (
              <AiTextToolInput
                key={key}
                disabled={isLoading}
                error={error}
                field={field}
                value={value}
                onChange={handleChangeFields}
              />
            );
          })}
        </FieldsGrid>
      )}
      <CTA
        disabled={isCTADisabled}
        icon={formSchema.cta.icon}
        isLoading={isLoading}
        label={formSchema.cta.label}
      />
    </Form>
  );
};

export default withEventScope(AiTextToolForm, { module: 'aiTextToolForm' });
